import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNotifications } from '../context/NotificationContext';
import api from '../services/api';
import MalwareTool from './MalwareTool';
import NetworkInjection from './NetworkInjection';
import Scanner from './Scanner';
import './BruteForceTools.css';

const BruteForceTools = () => {
  const { user, isInJail, getJailTimeRemaining } = useAuth();
  const { addNotification } = useNotifications();
  const [activeBruteForces, setActiveBruteForces] = useState([]);
  const [initialLoading, setInitialLoading] = useState(true);
  const [error, setError] = useState(null);
  const [bruteForcePartsCount, setBruteForcePartsCount] = useState(0);
  const [activeToolTab, setActiveToolTab] = useState('bruteforce');

  useEffect(() => {
    fetchBruteForceStatus();
    // Set up auto-refresh every 30 seconds
    const interval = setInterval(fetchBruteForceStatus, 30000);
    return () => clearInterval(interval);
  }, [user]);

  const fetchBruteForceStatus = async () => {
    try {
      if (!user?.token) return;

      const response = await api.get('/treasure-hunt/brute-force/status');
      const data = response.data;
      if (data.success) {
        setActiveBruteForces(data.data.activeBruteForces);
        setBruteForcePartsCount(data.data.bruteForcePartsCount);
      }
    } catch (err) {
      console.error('Error fetching brute force status:', err);
      setError('Failed to load brute force status');
    } finally {
      setInitialLoading(false);
    }
  };

  const checkAndCollectResults = async (targetId, targetType) => {
    try {
      const response = await api.get(`/treasure-hunt/brute-force/check?targetId=${targetId}&targetType=${targetType}`);
      const data = response.data;
      if (data.success && data.data.completed) {
          // Show notification for brute force completion
          const rewardDetails = [];
          if (data.data.code) {
            rewardDetails.push(`Access Code: ${data.data.code}`);
          }
          if (data.data.wrCoinsGained && data.data.wrCoinsGained > 0) {
            rewardDetails.push(`+${data.data.wrCoinsGained} WR Coins`);
          }
          rewardDetails.push(`+${data.data.xpGained || 50} XP`);
          
          addNotification({
            type: 'brute_force_completed',
            category: 'hacking',
            title: targetType === 'bank' ? '🏦 Bank Access Gained!' : '🎉 Code Cracked Successfully!',
            message: `Successfully accessed ${targetType.toUpperCase()} ${targetId}`,
            details: rewardDetails.join(' | '),
            icon: targetType === 'bank' ? '🏦' : '🔓',
            color: '#4CAF50'
          });
          // Refresh the status to remove completed attack
          fetchBruteForceStatus();
        }
    } catch (err) {
      console.error('Error checking brute force results:', err);
      addNotification({
        type: 'brute_force_check_error',
        category: 'error',
        title: '🌐 Network Error',
        message: 'Failed to check brute force results',
        details: 'Please check your connection and try again',
        icon: '📡',
        color: '#FF9800'
      });
    }
  };

  const stopBruteForce = async (targetId, targetType) => {
    if (!window.confirm(`Are you sure you want to stop the brute force attack on ${targetType.toUpperCase()} ${targetId}?\n\nThis will cancel the attack and you'll need to start a new one next month.`)) {
      return;
    }

    try {
      const response = await api.post('/treasure-hunt/brute-force/stop', { targetId, targetType });
      const data = response.data;
      if (data.success) {
        addNotification({
          type: 'brute_force_stopped',
          category: 'hacking',
          title: '✅ Brute Force Stopped',
          message: 'Attack cancelled successfully',
          details: 'You can start a new brute force attack again this month since you stopped voluntarily',
          icon: '🛑',
          color: '#FF9800'
        });
        // Refresh the status to update the display
        fetchBruteForceStatus();
      }
    } catch (err) {
      console.error('Error stopping brute force:', err);
      const errorMessage = err.response?.data?.message || 'Unknown error occurred';
      addNotification({
        type: 'brute_force_stop_error',
        category: 'error',
        title: '❌ Stop Attack Failed',
        message: 'Failed to stop brute force attack',
        details: errorMessage,
        icon: '⚠️',
        color: '#F44336'
      });
    }
  };

  const clearAllBruteForces = async () => {
    if (!window.confirm(`Are you sure you want to clear ALL active brute force operations?\n\nThis will cancel all running attacks and reset your monthly usage limits.\n\nYou'll be able to start new attacks immediately.`)) {
      return;
    }

    try {
      const response = await api.post('/treasure-hunt/brute-force/clear-all');
      const data = response.data;
      if (data.success) {
        addNotification({
          type: 'brute_force_cleared',
          category: 'hacking',
          title: '🧹 All Operations Cleared',
          message: 'All brute force attacks cleared successfully',
          details: `Cleared ${data.data.clearedCount} attacks. Monthly usage reset to allow new operations.`,
          icon: '✨',
          color: '#4CAF50'
        });
        // Refresh the status to update the display
        fetchBruteForceStatus();
      }
    } catch (err) {
      console.error('Error clearing all brute force attacks:', err);
      const errorMessage = err.response?.data?.message || 'Unknown error occurred';
      addNotification({
        type: 'brute_force_clear_error',
        category: 'error',
        title: '❌ Clear All Failed',
        message: 'Failed to clear brute force operations',
        details: errorMessage,
        icon: '⚠️',
        color: '#F44336'
      });
    }
  };

  const formatTimeRemaining = (estimatedCompletion) => {
    const now = new Date();
    const completion = new Date(estimatedCompletion);
    const timeRemaining = completion.getTime() - now.getTime();
    
    if (timeRemaining <= 0) {
      return 'Completed! Click to collect.';
    }
    
    const days = Math.floor(timeRemaining / (24 * 60 * 60 * 1000));
    const hours = Math.floor((timeRemaining % (24 * 60 * 60 * 1000)) / (60 * 60 * 1000));
    const minutes = Math.floor((timeRemaining % (60 * 60 * 1000)) / (60 * 1000));
    
    if (days > 0) {
      return `${days}d ${hours}h ${minutes}m`;
    } else if (hours > 0) {
      return `${hours}h ${minutes}m`;
    } else {
      return `${minutes}m`;
    }
  };

  const getProgressPercentage = (startTime, estimatedCompletion) => {
    const start = new Date(startTime).getTime();
    const end = new Date(estimatedCompletion).getTime();
    const now = new Date().getTime();
    
    const totalTime = end - start;
    const elapsed = now - start;
    
    return Math.min(100, Math.max(0, (elapsed / totalTime) * 100));
  };

  // Only show loading on first visit, not on refreshes
  if (initialLoading) {
    return (
      <div className="brute-force-tools">
        <div className="loading">Loading brute force tools...</div>
      </div>
    );
  }

  const renderToolContent = () => {
    if (activeToolTab === 'malware') {
      return <MalwareTool />;
    } else if (activeToolTab === 'network') {
      return <NetworkInjection />;
    } else if (activeToolTab === 'scanner') {
      return <Scanner />;
    }
    
    return (
      <>
        {/* Stats Overview */}
        <div className="brute-force-stats">
          <div className="stat-card">
            <h3>🔧 Equipment</h3>
            <div className="stat-value">{bruteForcePartsCount}</div>
            <div className="stat-label">Brute Force Parts</div>
          </div>
          <div className="stat-card">
            <h3>⚡ Active Attacks</h3>
            <div className="stat-value">{activeBruteForces.length}</div>
            <div className="stat-label">Running Attacks</div>
          </div>
          <div className="stat-card">
            <h3>⏱️ Attack Time</h3>
            <div className="stat-value">ATM: 20min</div>
            <div className="stat-label">Bank/Chest: 16-30 days</div>
          </div>
        </div>

        {error && (
          <div className="error-message">
            {error}
          </div>
        )}

        {/* Jail Status Warning */}
        {isInJail() && (
          <div className="jail-warning" style={{
            background: '#ff4444',
            color: 'white',
            padding: '15px',
            margin: '10px 0',
            borderRadius: '8px',
            border: '2px solid #ff6666'
          }}>
            <h3>🔒 You are currently in jail!</h3>
            <p>Time remaining: {getJailTimeRemaining()} minutes</p>
            <p>You cannot start new brute force attacks while incarcerated.</p>
          </div>
        )}

        {/* Active Brute Force Attacks */}
        <div className="active-attacks">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
            <h3>🚀 Active Brute Force Operations</h3>
            {activeBruteForces.length > 0 && (
              <button 
                className="clear-all-button"
                onClick={clearAllBruteForces}
                style={{
                  backgroundColor: '#FF6B6B',
                  color: 'white',
                  border: 'none',
                  padding: '8px 16px',
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontWeight: 'bold',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                }}
                onMouseOver={(e) => e.target.style.backgroundColor = '#FF5252'}
                onMouseOut={(e) => e.target.style.backgroundColor = '#FF6B6B'}
              >
                🧹 Clear All
              </button>
            )}
          </div>
          
          {activeBruteForces.length === 0 ? (
            <div className="no-attacks">
              <div className="no-attacks-content">
                <h4>🔍 No Active Attacks</h4>
                <p>You don't have any brute force attacks running.</p>
                <p>Visit <strong>Hack The Planet</strong> and click on treasure chests, banks, or ATMs to start new attacks.</p>
              </div>
            </div>
          ) : (
            <div className="attacks-grid">
              {activeBruteForces.map((attack, index) => {
                const isCompleted = attack.completed || new Date() >= new Date(attack.estimatedCompletion);
                const progress = getProgressPercentage(attack.startTime, attack.estimatedCompletion);
                
                return (
                  <div 
                    key={`${attack.targetType}-${attack.targetId}`} 
                    className={`attack-card ${isCompleted ? 'completed' : 'running'}`}
                  >
                    <div className="attack-header">
                      <h4>
                        {attack.targetType === 'chest' ? '💰' : attack.targetType === 'atm' ? '🏧' : '🏦'} 
                        {attack.targetType.toUpperCase()} #{attack.targetId}
                      </h4>
                      <div className={`status-badge ${isCompleted ? 'completed' : attack.connectionLost ? 'connection-lost' : 'running'}`}>
                        {isCompleted ? '✅ Ready' : attack.connectionLost ? '📡 Connection Lost' : '⚡ Running'}
                      </div>
                    </div>
                    
                    <div className="attack-progress">
                      <div className="progress-bar">
                        <div 
                          className="progress-fill" 
                          style={{ width: attack.connectionLost ? '0%' : `${progress}%` }}
                        ></div>
                      </div>
                      <div className="progress-text">
                        {attack.connectionLost ? 'No Signal' : `${Math.round(progress)}% Complete`}
                      </div>
                    </div>
                    
                    <div className="attack-details">
                      <div className="detail-row">
                        <span className="label">Started:</span>
                        <span className="value">
                          {new Date(attack.startTime).toLocaleDateString()} {new Date(attack.startTime).toLocaleTimeString()}
                        </span>
                      </div>
                      <div className="detail-row">
                        <span className="label">Time Remaining:</span>
                        <span className="value">
                          {attack.connectionLost ? 'Connection Lost' : formatTimeRemaining(attack.estimatedCompletion)}
                        </span>
                      </div>
                      {attack.connectionLost && (
                        <div className="detail-row">
                          <span className="label" style={{color: '#ff4444'}}>⚠️ Status:</span>
                          <span className="value" style={{color: '#ff4444'}}>
                            Move computer within 5000m to resume
                          </span>
                        </div>
                      )}
                    </div>
                    
                    {isCompleted ? (
                      <button 
                        className="collect-button"
                        onClick={() => checkAndCollectResults(attack.targetId, attack.targetType)}
                      >
                        🎯 Gain Access
                      </button>
                    ) : attack.connectionLost ? (
                      <button 
                        className="connection-lost-button"
                        disabled
                      >
                        📡 Connection Lost - Move Computer
                      </button>
                    ) : (
                      <button 
                        className="stop-button"
                        onClick={() => stopBruteForce(attack.targetId, attack.targetType)}
                      >
                        ❌ Stop Attack
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Equipment Info */}
        <div className="equipment-info">
          <h3>🛠️ Equipment Information</h3>
          <div className="equipment-details">
            <div className="equipment-stat">
              <span className="equipment-label">Brute Force Parts Owned:</span>
              <span className="equipment-value">{bruteForcePartsCount}</span>
            </div>
            <div className="equipment-stat">
              <span className="equipment-label">Time Reduction:</span>
              <span className="equipment-value">-{bruteForcePartsCount * 1} days</span>
            </div>
            <div className="equipment-stat">
              <span className="equipment-label">Attack Time Range:</span>
              <span className="equipment-value">16-30 days (random)</span>
            </div>
          </div>
          <p className="equipment-note">
            💡 Purchase more brute force parts in the <strong>Shop</strong> to reduce attack times. 
            Each part reduces the random attack time by 1 day. Only one brute force attack allowed per month.
          </p>
        </div>
      </>
    );
  };

  // Remove second loading check - let tools render with empty states if needed

  return (
    <div className="brute-force-tools">
      <div className="tools-header">
        <h2>🛠️ Hacking Tools Center</h2>
        <p>Access your brute force and malware attack tools from this centralized interface.</p>
      </div>

      {/* Tool Tab Navigation */}
      <div className="tool-tabs">
        <button 
          className={`tool-tab ${activeToolTab === 'bruteforce' ? 'active' : ''}`}
          onClick={() => setActiveToolTab('bruteforce')}
        >
          💻 Brute Force
        </button>
        <button 
          className={`tool-tab ${activeToolTab === 'malware' ? 'active' : ''}`}
          onClick={() => setActiveToolTab('malware')}
        >
          🦠 Malware
        </button>
        <button 
          className={`tool-tab ${activeToolTab === 'network' ? 'active' : ''}`}
          onClick={() => setActiveToolTab('network')}
        >
          🌐 Network Injection
        </button>
        <button 
          className={`tool-tab ${activeToolTab === 'scanner' ? 'active' : ''}`}
          onClick={() => setActiveToolTab('scanner')}
        >
          🔍 Scanner
        </button>
      </div>

      {/* Tool Content */}
      <div className="tool-content">
        {renderToolContent()}
      </div>
    </div>
  );
};

export default BruteForceTools;